<template>
  <v-container fluid class="fill-height align-start pa-0">
    <v-row id="checkout" class="ma-0 fill-height">
      <v-col id="flow" cols="12" md="8">
        <v-stepper v-if="!order && !version.foc" :model-value="currentStep" class="elevation-0 mb-5">
          <v-stepper-header>
            <v-stepper-item
              :value="1"
              title="Information"
              :editable="currentStep == 2"
              :complete="currentStep > 1"
              @click="currentStep = 1"
            />
            <v-divider></v-divider>
            <v-stepper-item :value="2" title="Payment" :complete="currentStep == 3" />
            <v-divider></v-divider>
            <v-stepper-item :value="3" title="Confirmation" :complete="currentStep == 3" />
          </v-stepper-header>
        </v-stepper>
        <transition name="fade" mode="out-in">
          <component
            :quote="quote"
            :version="version"
            v-model:stripeObjects="stripeObjects"
            :elements="elements"
            :stripe="stripe"
            v-model:form="form"
            :order="activeOrder"
            :newOrder="newOrder"
            :foc="version.foc"
            :region="quote.region"
            :is="stepComponents[currentStep - 1]"
            :key="currentStep"
            @success="flowSuccess"
            @back="currentStep--"
          ></component>
        </transition>
      </v-col>
      <v-col id="summary" cols="12" md="4">
        <Summary
          :quote="quote"
          :version="version"
          :order="order"
          :balanceDue="balanceDue"
          :newOrder="newOrder"
          :currentStep="currentStep"
        ></Summary>
      </v-col>
    </v-row>

    <v-overlay
      :model-value="true"
      v-if="submitting"
      persistent
      class="align-center justify-center text-center"
      opacity=".95"
      scrim="primary"
    >
      <div v-if="!error && !cardErrorMessage" class="text-center">
        <h3>Processing Order - Do Not Leave Or Refresh The Page</h3>
        <v-progress-circular class="mt-4" :size="50" :width="3" color="accent" indeterminate></v-progress-circular>
        <div class="label mt-4">{{ progressText }}</div>
      </div>
      <div v-if="error" class="text-center">
        <h3>There was an error placing your order</h3>
        <p>Your card has <b>NOT</b> been charged.</p>
        <p>
          We have been made aware of the problem and will contact you via email to resolve the issue. Please do not try
          and place your order again.
        </p>
      </div>
      <div v-if="cardErrorMessage" id="card-declined" class="text-center mx-auto">
        <v-icon size="64" color="accent">mdi-credit-card-remove-outline</v-icon>
        <h3 class="mt-4">Your payment was declined</h3>
        <p class="mt-3 mb-0">
          <b>{{ cardErrorMessage }}</b>
        </p>
        <p class="mt-3">
          No payment has been taken. Please check your card details and try again, or use a different card.
        </p>
        <v-btn class="mt-2" color="accent" variant="flat" @click="dismissCardError">Try Again</v-btn>
      </div>
    </v-overlay>

    <v-overlay
      :model-value="true"
      v-if="calculatingSalesTax"
      class="align-center justify-center text-center"
      opacity=".95"
      scrim="primary"
    >
      <div v-if="!salesTaxError">
        <v-progress-circular :size="50" :width="3" color="accent" indeterminate></v-progress-circular>
        <div v-if="calculatingSalesTax" class="text-body-1 text-center mt-4">Calculating Sales Tax</div>
      </div>
      <div v-else>
        <p>{{ salesTaxError }}</p>
        <v-btn
          color="accent"
          @click="
            () => {
              salesTaxError = null
              calculatingSalesTax = false
            }
          "
          >Try Again</v-btn
        >
      </div>
    </v-overlay>
  </v-container>
</template>

<script>
import * as MaterialResolver from '@/js/MaterialResolver'
import * as WorktopResolver from '@/js/WorktopResolver'
import { db, functions } from '@/js/db'
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import Summary from '@/components/customer/checkout/Summary'

import ContactInformationEU from '@/components/customer/checkout/ContactInformationEU'
import ContactInformationUS from '@/components/customer/checkout/ContactInformationUS'

import PaymentEU from '@/components/customer/checkout/PaymentEU'
import PaymentUS from '@/components/customer/checkout/PaymentUS'

import Confirmation from '@/components/customer/checkout/Confirmation'
import { useGoTo } from 'vuetify'

export default {
  name: 'Checkout',
  components: {
    ContactInformationEU,
    ContactInformationUS,
    PaymentEU,
    PaymentUS,
    Confirmation,
    Summary
  },
  props: ['quote', 'version', 'order'],
  setup() {
    return { goTo: useGoTo() }
  },
  data() {
    return {
      stripe: null,
      elements: null,
      currentStep: 1,
      submitting: false,
      error: false,
      cardErrorMessage: null,
      progressText: 'Placing Order',
      stepComponents:
        this.quote.region === 'us'
          ? ['ContactInformationUS', 'PaymentUS', 'Confirmation']
          : ['ContactInformationEU', 'PaymentEU', 'Confirmation'],
      form: {
        email: this.quote.customerEmail,
        billingSameAsDelivery: false,
        deliveryCountry: this.quote.region == 'us' ? 'US' : null,
        bankTransfer: this.order ? (this.order.bankTransfer ? 1 : 0) : this.version.allowBankTransfer ? 1 : 0
      },
      addressDetails: null,
      billingAddressDetails: null,
      stripeObjects: {
        cardName: null,
        cardNumber: null,
        cardExpiry: null,
        cardCvc: null
      },
      calculatingSalesTax: false,
      salesTaxError: null,
      newOrder: false,
      createdOrder: null
    }
  },
  computed: {
    activeOrder() {
      return this.order || this.createdOrder
    },
    balanceDue() {
      if (!this.order) return null
      const total =
        this.order.region === 'us' && this.version.pricing.salesTax
          ? this.version.pricing.total + this.version.pricing.salesTax.amount_to_collect
          : this.version.pricing.total

      return this.$utils.calculateBalanceDue(total, this.order.payments)
    }
  },
  watch: {
    'order.bankTransfer'(val) {
      // Quote team toggle live-updates the customer's view mid-checkout
      this.form.bankTransfer = val ? 1 : 0
    }
  },
  async mounted() {
    // Populate email & phone/mobile numbers to form, if previous order exists
    this.form.email = this.quote.customerEmail
    if (this.quote.orders && Object.values(this.quote.orders).length > 0) {
      const orders = Object.values(this.quote.orders)
      const lastOrder = await getDoc(doc(db, 'orders', orders[orders.length - 1].orderID))

      // there will be a lastOrder so set this.addressDetails
      this.addressDetails = {
        firstName: lastOrder.data().customerContactDetails?.firstName,
        lastName: lastOrder.data().customerContactDetails?.lastName,
        address: {
          line1: lastOrder.data().customerContactDetails?.deliveryAddressLine1,
          line2: lastOrder.data().customerContactDetails?.deliveryAddressLine2,
          city: lastOrder.data().customerContactDetails?.deliveryCity,
          country: lastOrder.data().customerContactDetails?.deliveryCountry,
          postal_code: lastOrder.data().customerContactDetails?.deliveryPostcode,
          state: lastOrder.data().customerContactDetails?.deliveryState
        }
      }

      this.form.mobile = lastOrder.data().customerContactDetails.mobile

      if (this.quote.region === 'us') {
        this.form.phone1 = lastOrder.data().customerContactDetails.phone1
        delete this.form.mobile
      }
    }
  },
  created() {
    const pk_live = this.quote.region === 'us' ? 'pk_live_wkqHseRJMGkI2NjoWSHeSulB' : 'pk_live_aewsxnqC09OfkMgkq2rpN5Kw'
    const pk_test = this.quote.region === 'us' ? 'pk_test_YeXX1cZIcegbpp3n4u4oMSff' : 'pk_test_b3KTo1cFHBzSScUAa3zN4VrR'

    let stripe_pk = window.location.hostname == 'mega.plykea.com' ? pk_live : pk_test

    // Ignore error from globally available Stripe lib
    // eslint-disable-next-line
    this.stripe = Stripe(stripe_pk)

    this.elements = this.stripe.elements({
      fonts: [{ cssSrc: 'https://use.typekit.net/uxg0dfx.css' }]
    })

    if (this.order) {
      this.currentStep = this.order.bankTransfer ? 3 : 2
    }
  },
  methods: {
    billingName() {
      if (this.billingAddressDetails?.name) return this.billingAddressDetails.name
      const parts = [this.billingAddressDetails?.firstName, this.billingAddressDetails?.lastName]
      return parts.filter(Boolean).join(' ') || null
    },
    dismissCardError() {
      this.submitting = false
      this.cardErrorMessage = null
    },
    async flowSuccess(form, addressDetails) {
      // flowSuccess will nevber be triggered from step 3 (confirmation)
      if (this.currentStep == 1) {
        this.addressDetails = addressDetails

        // if US order, calculate sales tax
        if (this.quote.region == 'us') {
          const salesTaxResult = await this.calculateSalesTax()
          if (!salesTaxResult) return
        }

        // if FOC
        if (this.version.foc) {
          this.submitting = true
          // customerCheck must be true
          await this.createOrder()
          // collected customerContactDetails in step 1, so go to step 3 for confirmation.
          this.currentStep = 3
          this.submitting = false
        } else {
          this.currentStep++
          this.goTo(0) // just scroll to top of page
        }
      } else if (this.currentStep == 2) {
        // US: never take payment without a valid sales tax figure on the order.
        if (this.quote.region === 'us') {
          const st = this.version.pricing?.salesTax
          const hasValidSalesTax = st && st.amount_to_collect != null && st.rate != null
          if (!hasValidSalesTax) {
            const ok = await this.calculateSalesTax()
            if (!ok) return
          }
        }

        this.error = false
        this.cardErrorMessage = null

        this.submitting = true
        // customerCheck must be true
        this.goTo(0) // just scroll to top of page

        // set the billing address details as payment details come in from step 2
        if (!form.billingSameAsDelivery) {
          // success emit will return billing address details
          this.billingAddressDetails = addressDetails
          // if new order, billing address details are already set in createOrder()
          if (this.order) {
            if (
              this.order.customerContactDetails?.billingPostcode !== this.billingAddressDetails.address?.postal_code ||
              !this.order.customerContactDetails?.billingPostcode
            ) {
              const orderRef = doc(db, 'orders', this.order.id)
              await updateDoc(orderRef, {
                customerContactDetails: {
                  ...this.order.customerContactDetails,
                  balanceBillingSameAsDelivery: false,
                  balanceBillingDetails: {
                    billingAddressLine1: this.billingAddressDetails.address.line1,
                    billingAddressLine2: this.billingAddressDetails.address.line2,
                    billingCity: this.billingAddressDetails.address.city,
                    billingCountry: this.billingAddressDetails.address.country,
                    billingPostcode: this.billingAddressDetails.address.postal_code,
                    billingState: this.billingAddressDetails.address.state
                  }
                }
              })
            }
          }
        } else if (form.billingSameAsDelivery) {
          // otherwise use delivery address as billing address
          if (this.addressDetails) {
            this.billingAddressDetails = this.addressDetails
          } else {
            // coming from 2nd payment so order already exists, use the delivery address from db
            if (this.order && this.order.customerContactDetails) {
              this.billingAddressDetails = {
                address: {
                  line1: this.order.customerContactDetails?.deliveryAddressLine1,
                  line2: this.order.customerContactDetails?.deliveryAddressLine2,
                  city: this.order.customerContactDetails?.deliveryCity,
                  country: this.order.customerContactDetails?.deliveryCountry,
                  postal_code: this.order.customerContactDetails?.deliveryPostcode,
                  state: this.order.customerContactDetails?.deliveryState
                },
                name: this.order.customerContactDetails?.firstName + ' ' + this.order.customerContactDetails?.lastName,
                email: this.order.customerContactDetails?.email
              }
            }

            if (
              !this.order.customerContactDetails?.balanceBillingDetails &&
              this.billingAddressDetails.address?.postal_code
            ) {
              if (
                this.order.customerContactDetails.billingAddressDetails?.address.postal_code !==
                this.billingAddressDetails.address.postal_code
              ) {
                const orderRef = doc(db, 'orders', this.order.id)
                await updateDoc(orderRef, {
                  customerContactDetails: {
                    ...this.order.customerContactDetails,
                    balanceBillingSameAsDelivery: true,
                    balanceBillingDetails: {
                      billingAddressLine1: this.order.customerContactDetails?.deliveryAddressLine1,
                      billingAddressLine2: this.order.customerContactDetails?.deliveryAddressLine2,
                      billingCity: this.order.customerContactDetails?.deliveryCity,
                      billingCountry: this.order.customerContactDetails?.deliveryCountry,
                      billingPostcode: this.order.customerContactDetails?.deliveryPostcode,
                      billingState: this.order.customerContactDetails?.deliveryState
                    }
                  }
                })
              }
            }
          }
        }

        const orderToPlace = this.activeOrder || (await this.createOrder())
        if (orderToPlace) {
          if (this.form.bankTransfer) {
            this.currentStep = 3
            this.submitting = false
          } else {
            await this.processPayment(orderToPlace)
          }
        }
      }
    },
    async createOrder() {
      // Confirmation component changes text dependent on this var
      this.newOrder = true

      try {
        MaterialResolver.pinMaterialIdsOnVersion(this.version)
        WorktopResolver.pinWorktopIdsOnVersion(this.version, this.quote.region)
        await updateDoc(doc(db, 'quotes', this.quote.id), { versions: this.quote.versions })

        // Create Order in Firebase
        this.progressText = 'Creating Order'
        const orderTotal =
          this.quote.region == 'us'
            ? this.version.pricing.total + this.version.pricing.salesTax.amount_to_collect
            : this.version.pricing.total

        // if FOC the billingAddressDetails will be null
        let addressInput = {
          firstName: this.addressDetails?.firstName,
          lastName: this.addressDetails?.lastName,
          billingAddressDetails: {
            name: this.billingName(),
            email: this.form?.email ? this.form?.email.toLowerCase() : '',
            address: {
              line1: this.billingAddressDetails?.address?.line1,
              line2: this.billingAddressDetails?.address?.line2,
              city: this.billingAddressDetails?.address?.city,
              country: this.billingAddressDetails?.address?.country,
              postal_code: this.billingAddressDetails?.address?.postal_code,
              state: this.billingAddressDetails?.address?.state
            }
          },
          deliveryAddressLine1: this.addressDetails?.address?.line1,
          deliveryAddressLine2: this.addressDetails?.address?.line2,
          deliveryCity: this.addressDetails?.address?.city,
          deliveryCountry: this.addressDetails?.address?.country,
          deliveryPostcode: this.addressDetails?.address?.postal_code,
          deliveryState: this.addressDetails?.address?.state
        }

        let orderObj = {
          balanceOutstanding: this.version.pricing.total > 0 && !this.version.foc,
          bankTransfer: !!this.form.bankTransfer,
          allowDeposit: this.version.allowDeposit,
          foc: this.version.foc,
          payments: [],
          numPayments: 0,
          quoteID: this.quote.id,
          region: this.quote.region,
          versionID: this.version.id,
          total: this.$utils.toFixedNumber(orderTotal, 2),
          customerContactDetails: {
            ...this.form,
            ...addressInput
          },
          customerEmail: this.quote.customerEmail.toLowerCase(),
          creatorEmail: this.version.creatorEmail,
          currency: this.version.currency,
          shipmentsCreated: false,
          type: 'mega-admin'
        }

        if (this.version.recut) {
          orderObj.recut = {
            cockupID: this.version.cockupID,
            originalVersionID: this.version.recutOriginalVersionID,
            originalOrderID: this.version.recutOriginalOrderID
          }
        }

        if (this.version.exchangeRates) {
          orderObj.exchangeRates = this.version.exchangeRates
        }

        if (this.quote.region === 'us') {
          orderObj.dispatchEta = null
          orderObj.inProduction = null
          orderObj.trackingNumber = null
          orderObj.shipped = false
        }

        const createNewOrderFunc = httpsCallable(functions, 'createNewOrder')
        const newOrder = await createNewOrderFunc(orderObj)

        this.createdOrder = newOrder.data
        this.$emit('orderCreated', newOrder.data.id)
        return newOrder.data
      } catch (e) {
        console.error(e)
        this.error = true
      }
    },
    async processPayment(order) {
      this.progressText = 'Processing Payment'

      try {
        let paymentAmount
        if (order.payments.length > 0) {
          paymentAmount = Math.round(this.balanceDue * 100)
        } else {
          const allowDeposit = order.allowDeposit ?? this.version.allowDeposit
          paymentAmount = allowDeposit ? Math.round(order.total * 50) : Math.round(order.total * 100)
        }

        // Create Payment Intent
        let getPaymentIntent = httpsCallable(functions, 'getPaymentIntent')

        let paymentIntent = await getPaymentIntent({
          amount: paymentAmount,
          currency: this.version.currency,
          description: 'Plykea Order - ' + order.orderNumber,
          metadata: {
            orderNumber: order.orderNumber,
            orderID: order.id
          }
        })

        const orderRef = doc(db, 'orders', order.id)
        await updateDoc(orderRef, {
          pendingPaymentIntentID: paymentIntent.data.id
        })

        // set up param for billing details
        let billingDetails = {
          address: {
            line1: this.billingAddressDetails?.address?.line1,
            line2: this.billingAddressDetails?.address?.line2,
            city: this.billingAddressDetails?.address?.city,
            country: this.billingAddressDetails?.address?.country,
            postal_code: this.billingAddressDetails?.address?.postal_code,
            state: this.billingAddressDetails?.address?.state
          },
          name: this.billingName(),
          email: this.form?.email.toLowerCase()
        }

        // Charge the card
        const cardPayment = await this.stripe.confirmCardPayment(paymentIntent.data.client_secret, {
          payment_method: {
            card: this.stripeObjects.cardNumber,
            billing_details: billingDetails
          }
        })

        if (cardPayment.error) {
          this.cardErrorMessage = cardPayment.error.message

          try {
            const logPaymentErrorFunc = httpsCallable(functions, 'logPaymentError')
            await logPaymentErrorFunc({
              email: order.customerContactDetails.email,
              error: cardPayment.error.message
            })
          } catch (e) {
            console.error('logPaymentError failed', e)
          }
          return
        }

        this.currentStep = 3
        this.submitting = false
      } catch (e) {
        this.$utils.slackError('Error processing card', this.form.email, e)
        this.error = true
      }
    },
    async calculateSalesTax() {
      const MAX_ATTEMPTS = 3
      this.salesTaxError = null
      this.calculatingSalesTax = true

      const calculateSalesTax = httpsCallable(functions, 'calculateSalesTax')

      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
          const salesTax = await calculateSalesTax({
            to_zip: this.addressDetails.address?.postal_code,
            to_state: this.addressDetails.address?.state,
            to_city: this.addressDetails.address?.city,
            to_street: this.addressDetails.address?.line1,
            amount: this.version.pricing.total - this.version.pricing.shipping,
            shipping: this.version.pricing.shipping,
            line_items: [
              {
                id: '1',
                quantity: 1,
                unit_price: this.version.pricing.total - this.version.pricing.shipping,
                discount: 0
              }
            ]
          })

          const data = salesTax.data
          const valid =
            data &&
            data.amount_to_collect != null &&
            data.rate != null &&
            (Number(data.amount_to_collect) === 0 || data.jurisdictions?.state)
          if (!valid) throw new Error('Incomplete sales tax result')

          this.version.pricing.salesTax = data
          const quoteRef = doc(db, 'quotes', this.quote.id)
          await setDoc(quoteRef, this.quote)

          this.calculatingSalesTax = false
          return true
        } catch (error) {
          this.$utils.slackError(`Sales tax calc failed (attempt ${attempt}/${MAX_ATTEMPTS})`, this.form.email, error)
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000))
          }
        }
      }

      // All attempts exhausted — keep the overlay up with a clear message and block payment.
      this.salesTaxError =
        'We were unable to calculate the sales tax for your order. Please contact your sales representative before completing your payment.'
      return false
    }
  }
}
</script>

<style lang="scss" scoped>
#card-declined {
  max-width: 420px;
  padding: 0 20px;
}

#checkout {
  & *:not(.v-icon) {
    font-family: 'proxima-nova', sans-serif;
    letter-spacing: 0.5px !important;
    -webkit-font-smoothing: antialiased;
    text-rendering: optimizeLegibility;
    font-variant-ligatures: none;
  }
  @media only screen and (max-width: 959.98px) {
    padding: 15px;
  }

  #flow {
    padding: 50px 100px 25px 100px;
  }

  #summary {
    background: $sand;
    padding: 70px 30px 25px 30px;
  }

  .v-stepper-item {
    &:first-of-type {
      padding-left: 0;
    }
    &:last-of-type {
      padding-right: 0;
    }
  }

  ::v-deep.form-group {
    border: solid 1px #e0e0e0;
    padding: 10px 20px 15px;
    border-radius: 5px;
    margin-top: 15px;
  }

  ::v-deep .v-text-field {
    padding-top: 18px;
  }

  ::v-deep .v-label--active {
    transform: translateY(-28px) scale(1);
    font-size: 14.88px;
    font-weight: 500;
    color: #30313d;
  }
}
</style>
